import { useEffect, useState } from 'react'
import { HelpOutline } from '@mui/icons-material'
import {
  Alert,
  Card,
  Divider,
  Grid2,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useGetLiveData } from '@/services/hooks/relay/dynamic'
import { OrgCredential } from '@/types/credentials'
import { LiveListEntity } from '@/types/live-lists'
import { BaseVendor } from '@/types/relay'
import { VENDOR_INFO_TYPE_IDS } from '@/utils/info-types'
import LiveListPicker from './LiveListPicker'
import VendorSettingsFields from './VendorSettingsFields'

type VendorSettingsProps = {
  selectedCredential: OrgCredential
  settingsOrgId: string
  isAuthenticated: boolean
}

export default function VendorSettings({
  selectedCredential,
  settingsOrgId,
  isAuthenticated,
}: VendorSettingsProps) {
  const [vendorSearchValue, setVendorSearchValue] = useState('')
  // Keep the selected vendor object so the picker value remains stable when
  // the current filtered page doesn't include the selected id.
  const [selectedVendor, setSelectedVendor] =
    useState<LiveListEntity<BaseVendor>>()

  const {
    allData: vendors,
    hasNextPage: hasNextVendorPage,
    fetchNextPage: fetchNextVendorPage,
    isLoading,
    isFetching: isFetchingVendors,
  } = useGetLiveData<BaseVendor>({
    infoTypeId: VENDOR_INFO_TYPE_IDS[0], // Since it's used as a hint, it's okay to pass any of them
    orgId: settingsOrgId,
    orgCredentialTypeId: selectedCredential.type_id,
    filters: {
      query: vendorSearchValue,
      limit: '100',
    },
    enabled: isAuthenticated,
  })

  useEffect(() => {
    // Reset selected vendor when switching credentials or orgs since vendor lists are credential-org-specific.
    setSelectedVendor(undefined)
  }, [selectedCredential?.id, settingsOrgId])

  useEffect(() => {
    if (!selectedVendor && vendors.length > 0) {
      setSelectedVendor(vendors[0])
    }
  }, [vendors, selectedVendor])

  return (
    <Card sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Vendor Settings</Typography>
          <Tooltip title="Vendor settings are managed per credential.">
            <HelpOutline sx={{ color: 'text.secondary' }} fontSize="small" />
          </Tooltip>
        </Stack>

        <Stack spacing={3}>
          {!isAuthenticated && (
            <Alert severity="warning" variant="outlined">
              The credential for {selectedCredential.org?.name} is not
              authenticated. Please authenticate it, and then refresh the page.
            </Alert>
          )}

          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <LiveListPicker
                value={selectedVendor}
                options={vendors}
                onChange={(entity) => {
                  setSelectedVendor(entity || undefined)
                }}
                searchValue={vendorSearchValue}
                setSearchValue={setVendorSearchValue}
                hasNextPage={hasNextVendorPage}
                fetchNextPage={fetchNextVendorPage}
                label="Vendor"
                isLoading={isLoading}
                isFetching={isFetchingVendors}
                disabled={!selectedVendor}
              />
            </Grid2>
          </Grid2>

          <Divider />

          <VendorSettingsFields
            credential={selectedCredential}
            settingsOrgId={settingsOrgId}
            vendor={selectedVendor}
          />
        </Stack>
      </Stack>
    </Card>
  )
}
